const safeStringify = (obj) =>
  JSON.stringify(obj).replace(/<\/script>/gi, '<\\/script>');

class ProductGridLazyVariants {
  static init() {
    if (this.initialized) return;
    this.initialized = true;

    this.initObserver();
    this.bindQuickBuyEvents();
    this.bindOutsideClickEvents();
    this.bindResizeEvents();
    this.bindMobileCloneEvents();
    this.bindMobileCloseEvents();
    this.updateMobileVisibility();
  }

  static initObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const item = entry.target;

          this.loadProductJson(item).then(() => {
            this.initItem(item);
          });

          observer.unobserve(item);
        });
      },
      {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0.01
      }
    );

    document.querySelectorAll('.js-product-grid-item').forEach((item) => {
      observer.observe(item);
    });

    window.observeGridItems = () => {
      document.querySelectorAll('.js-product-grid-item').forEach((item) => {
        observer.observe(item);
      });
    };
  }

  static updateMobileVisibility() {
    const mobileAddToCart = this.getMobileAddToCart();
    if (!mobileAddToCart) return;

    const isMobile = window.innerWidth < 992;
    const isActive = mobileAddToCart.classList.contains('active');

    if (isMobile) {
      mobileAddToCart.classList.remove('d-none');
    } else {
      mobileAddToCart.classList.add('d-none');
    }

    if (isMobile && !isActive) {
      mobileAddToCart.classList.add('d-none');
    }
  }

  static bindQuickBuyEvents() {
    if (this.quickBuyEventsBound) return;
    this.quickBuyEventsBound = true;

    document.addEventListener('click', (event) => {
      const button = event.target.closest('.js-product-quick-buy');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const item = button.closest('.js-product-grid-item');
      if (!item) return;

      const isOpen = item.classList.contains('product-grid-item--quick-buy');

      if (isOpen) {
        this.closeQuickBuy(item);
      } else {
        this.openQuickBuy(item);
      }
    });
  }

  static bindOutsideClickEvents() {
    if (this.outsideClickEventsBound) return;
    this.outsideClickEventsBound = true;

    this.shouldBlockNextClick = false;

    document.addEventListener(
      'pointerdown',
      (event) => {
        const mobileAddToCart = this.getMobileAddToCart();
        const isMobileAddToCartActive =
          mobileAddToCart && mobileAddToCart.classList.contains('active');

        const clickedInsideMobileAddToCart = event.target.closest('.js-mobile-add-to-cart');
        const clickedMobileClose = event.target.closest('.js-mobile-add-to-cart-close');
        const clickedInsideGridItem = event.target.closest('.js-product-grid-item');
        const clickedQuickBuyButton = event.target.closest('.js-product-quick-buy');

        if (clickedMobileClose) {
          event.preventDefault();
          event.stopPropagation();

          if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
          }

          this.closeMobileAddToCart();
          this.shouldBlockNextClick = true;
          return;
        }

        if (isMobileAddToCartActive) {
          if (clickedInsideMobileAddToCart) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();

          if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
          }

          this.closeMobileAddToCart();
          this.shouldBlockNextClick = true;
          return;
        }

        if (clickedInsideGridItem || clickedQuickBuyButton) {
          return;
        }

        document.querySelectorAll('.js-product-grid-item.product-grid-item--quick-buy').forEach((item) => {
          this.closeQuickBuy(item);
        });

        this.closeMobileAddToCart();
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        if (!this.shouldBlockNextClick) return;

        event.preventDefault();
        event.stopPropagation();

        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }

        this.shouldBlockNextClick = false;
      },
      true
    );
  }

  static bindResizeEvents() {
    if (this.resizeEventsBound) return;
    this.resizeEventsBound = true;

    let resizeRaf = null;

    window.addEventListener('resize', () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);

      resizeRaf = requestAnimationFrame(() => {
        document.querySelectorAll('.js-product-grid-item.product-grid-item--quick-buy').forEach((item) => {
          this.syncQuickBuyLayout(item);
          this.updateQuickBuyButtons(item);
        });

        if (window.innerWidth >= 992) {
          this.closeMobileAddToCart();
        }

        this.updateMobileVisibility();
      });
    });
  }

  static bindMobileCloneEvents() {
    if (this.mobileCloneEventsBound) return;
    this.mobileCloneEventsBound = true;

    document.addEventListener('click', (event) => {
      const mobileRoot = event.target.closest('.js-mobile-add-to-cart');
      if (!mobileRoot) return;

      if (event.target.closest('.js-mobile-add-to-cart-close')) {
        event.preventDefault();
        event.stopPropagation();
        this.closeMobileAddToCart();
        return;
      }

      const sourceProductId = mobileRoot.getAttribute('data-source-product-id');
      if (!sourceProductId) return;

      const sourceItem = document.querySelector(`.js-product-grid-item[data-product-id="${sourceProductId}"]`);
      if (!sourceItem) return;

      const color = event.target.closest('.js-product-color');
      const size = event.target.closest('.js-product-size');
      const addToCart = event.target.closest('.js-add-to-cart');
      const outOfStock = event.target.closest('.js-out-of-stock');

      if (color || size) {
        event.preventDefault();
        event.stopPropagation();

        const optionEl = color || size;

        if (optionEl.classList.contains('is-disabled')) return;

        const option = optionEl.getAttribute('data-option');
        const value = optionEl.getAttribute('data-value');

        if (!option || !value) return;

        const selector = color ? '.js-product-color' : '.js-product-size';

        const originalEl = sourceItem.querySelector(
          `${selector}[data-option="${CSS.escape(option)}"][data-value="${CSS.escape(value)}"]`
        );

        if (originalEl) {
          originalEl.click();
        }

        return;
      }

      if (addToCart) {
        event.preventDefault();
        event.stopPropagation();

        const originalAddToCart = sourceItem.querySelector('.js-add-to-cart:not(.d-none)');
        if (originalAddToCart) {
          originalAddToCart.click();
        }

        return;
      }

      if (outOfStock) {
        event.preventDefault();
        event.stopPropagation();

        const href = outOfStock.getAttribute('href');
        if (href) {
          window.location.href = href;
          return;
        }

        const originalOutOfStock = sourceItem.querySelector('.js-out-of-stock');
        if (originalOutOfStock && originalOutOfStock.getAttribute('href')) {
          window.location.href = originalOutOfStock.getAttribute('href');
        }
      }
    });
  }

  static bindMobileCloseEvents() {
    if (this.mobileCloseEventsBound) return;
    this.mobileCloseEventsBound = true;

    document.addEventListener('click', (event) => {
      const closeButton = event.target.closest('.js-mobile-add-to-cart-close');
      if (!closeButton) return;

      event.preventDefault();
      event.stopPropagation();
      this.closeMobileAddToCart();
    });
  }

  static getMobileAddToCart() {
    return document.querySelector('.js-mobile-add-to-cart');
  }

  static lockBodyScroll() {
    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  }

  static unlockBodyScroll() {
    const body = document.body;

    if (body.classList.contains('mobile-menu-open')) return;

    const headerSearch = document.querySelector('.js-header-search');
    if (headerSearch && headerSearch.classList.contains('active')) return;

    const drawerCart = document.querySelector('.js-drawer-cart');
    if (drawerCart && drawerCart.classList.contains('active')) return;

    if (document.querySelector('.js-product-tab.active')) return;

    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }

  static ensureMobileCloseButton() {
    const mobileAddToCart = this.getMobileAddToCart();
    if (!mobileAddToCart) return;

    let closeButton = mobileAddToCart.querySelector('.js-mobile-add-to-cart-close');

    if (!closeButton) {
      closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'mobile-add-to-cart__close js-mobile-add-to-cart-close';
      closeButton.setAttribute('aria-label', 'Close');
      closeButton.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.75 11.2352L5.99263 5.99264M5.99263 5.99264L11.2352 0.75M5.99263 5.99264L0.75 0.75M5.99263 5.99264L11.2352 11.2352" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      mobileAddToCart.prepend(closeButton);
    }
  }

  static clearMobileAddToCart() {
    const mobileAddToCart = this.getMobileAddToCart();
    if (!mobileAddToCart) return;

    mobileAddToCart.innerHTML = '';
  }

  static openMobileAddToCart() {
    const mobileAddToCart = this.getMobileAddToCart();
    if (!mobileAddToCart) return;

    mobileAddToCart.classList.remove('d-none');
    mobileAddToCart.classList.remove('active');

    setTimeout(() => {
      mobileAddToCart.classList.add('active');
    }, 50);

    this.lockBodyScroll();
  }

  static closeMobileAddToCart() {
    const mobileAddToCart = this.getMobileAddToCart();
    if (!mobileAddToCart) {
      this.unlockBodyScroll();
      return;
    }

    const sourceProductId = mobileAddToCart.getAttribute('data-source-product-id');

    if (sourceProductId) {
      const sourceItem = document.querySelector(`.js-product-grid-item[data-product-id="${sourceProductId}"]`);

      if (sourceItem) {
        sourceItem.classList.remove('product-grid-item--quick-buy');
        this.resetQuickBuyButtons(sourceItem);
      }
    }

    mobileAddToCart.classList.remove('active');
    mobileAddToCart.removeAttribute('data-source-product-id');

    this.unlockBodyScroll();

    setTimeout(() => {
      this.updateMobileVisibility();
    }, 300);
  }

  static openQuickBuy(item) {
    document.querySelectorAll('.js-product-grid-item.product-grid-item--quick-buy').forEach((openedItem) => {
      if (openedItem !== item) {
        this.closeQuickBuy(openedItem);
      }
    });

    item.classList.add('product-grid-item--quick-buy');
    this.syncQuickBuyLayout(item);
    this.updateQuickBuyButtons(item);
  }

  static closeQuickBuy(item) {
    if (!item) return;

    item.classList.remove('product-grid-item--quick-buy');
    this.resetQuickBuyButtons(item);

    const mobileAddToCart = this.getMobileAddToCart();
    if (
      mobileAddToCart &&
      mobileAddToCart.getAttribute('data-source-product-id') === item.getAttribute('data-product-id')
    ) {
      mobileAddToCart.classList.remove('active');
      mobileAddToCart.removeAttribute('data-source-product-id');
      this.unlockBodyScroll();
    }
  }

  static syncQuickBuyLayout(item) {
    if (!item) return;

    if (window.innerWidth < 992) {
      const mobileAddToCart = this.getMobileAddToCart();
      const productId = item.getAttribute('data-product-id');

      if (mobileAddToCart?.getAttribute('data-source-product-id') !== productId) {
        this.renderMobileQuickBuy(item);
      } else {
        this.syncMobileQuickBuyState(item);
      }
    } else {
      const mobileAddToCart = this.getMobileAddToCart();
      if (
        mobileAddToCart &&
        mobileAddToCart.getAttribute('data-source-product-id') === item.getAttribute('data-product-id')
      ) {
        this.closeMobileAddToCart();
      }
    }
  }

  static renderMobileQuickBuy(item) {
    const mobileAddToCart = this.getMobileAddToCart();
    if (!mobileAddToCart || !item) return;

    const productId = item.getAttribute('data-product-id') || '';

    const content = item.querySelector('.js-product-grid-item-content');
    const options = item.querySelector('.js-product-grid-item-options');
    const addToCartWrapper = item.querySelector('.product-grid-item__add-to-cart');

    this.clearMobileAddToCart();
    mobileAddToCart.setAttribute('data-source-product-id', productId);

    this.ensureMobileCloseButton();

    if (content) {
      const contentClone = content.cloneNode(true);
      contentClone.classList.add('is-mobile-quick-buy-clone');
      mobileAddToCart.appendChild(contentClone);
    }

    if (options) {
      const optionsClone = options.cloneNode(true);
      optionsClone.classList.add('is-mobile-quick-buy-clone');
      mobileAddToCart.appendChild(optionsClone);
    }

    if (addToCartWrapper) {
      const addToCartClone = addToCartWrapper.cloneNode(true);
      addToCartClone.classList.add('is-mobile-quick-buy-clone');
      mobileAddToCart.appendChild(addToCartClone);
    }

    this.openMobileAddToCart();
    this.syncMobileQuickBuyState(item);
  }

  static syncMobileQuickBuyState(sourceItem) {
    const mobileAddToCart = this.getMobileAddToCart();
    if (!mobileAddToCart || !sourceItem) return;

    const sourceProductId = sourceItem.getAttribute('data-product-id');
    if (!sourceProductId) return;

    if (mobileAddToCart.getAttribute('data-source-product-id') !== sourceProductId) return;

    const syncOptions = (selector) => {
      const sourceOptions = sourceItem.querySelectorAll(selector);
      const mobileOptions = mobileAddToCart.querySelectorAll(selector);

      mobileOptions.forEach((mobileEl) => {
        const option = mobileEl.getAttribute('data-option');
        const value = mobileEl.getAttribute('data-value');

        if (!option || !value) return;

        const sourceEl = Array.from(sourceOptions).find((el) => {
          return (
            el.getAttribute('data-option') === option &&
            el.getAttribute('data-value') === value
          );
        });

        if (!sourceEl) return;

        mobileEl.classList.toggle('active', sourceEl.classList.contains('active'));
        mobileEl.classList.toggle('is-disabled', sourceEl.classList.contains('is-disabled'));

        if (sourceEl.hasAttribute('aria-disabled')) {
          mobileEl.setAttribute('aria-disabled', sourceEl.getAttribute('aria-disabled'));
        } else {
          mobileEl.removeAttribute('aria-disabled');
        }
      });
    };

    syncOptions('.js-product-color');
    syncOptions('.js-product-size');

    const sourceAddToCart = sourceItem.querySelector('.js-add-to-cart');
    const sourceOutOfStock = sourceItem.querySelector('.js-out-of-stock');

    const mobileAddToCartButton = mobileAddToCart.querySelector('.js-add-to-cart');
    const mobileOutOfStockButton = mobileAddToCart.querySelector('.js-out-of-stock');

    if (mobileAddToCartButton && sourceAddToCart) {
      mobileAddToCartButton.classList.toggle('d-none', sourceAddToCart.classList.contains('d-none'));
    }

    if (mobileOutOfStockButton && sourceOutOfStock) {
      mobileOutOfStockButton.classList.toggle('d-none', sourceOutOfStock.classList.contains('d-none'));

      const href = sourceOutOfStock.getAttribute('href');
      if (href) {
        mobileOutOfStockButton.setAttribute('href', href);
      }
    }

    const sourcePriceNew = sourceItem.querySelector('.js-price-new');
    const sourcePriceOld = sourceItem.querySelector('.js-price-old');

    const mobilePriceNew = mobileAddToCart.querySelector('.js-price-new');
    const mobilePriceOld = mobileAddToCart.querySelector('.js-price-old');

    if (mobilePriceNew && sourcePriceNew) {
      mobilePriceNew.innerHTML = sourcePriceNew.innerHTML;
      mobilePriceNew.className = sourcePriceNew.className;
    }

    if (mobilePriceOld && sourcePriceOld) {
      mobilePriceOld.innerHTML = sourcePriceOld.innerHTML;
      mobilePriceOld.className = sourcePriceOld.className;
    }

    const currentVariantId = sourceItem.dataset.currentVariantId || '';

    const mobileHiddenVariantInput = mobileAddToCart.querySelector('.js-current-variant-id');
    if (mobileHiddenVariantInput) {
      mobileHiddenVariantInput.value = currentVariantId;
    }

    const mobileFormVariantInput = mobileAddToCart.querySelector('form [name="id"]');
    if (mobileFormVariantInput) {
      mobileFormVariantInput.value = currentVariantId;
      mobileFormVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const mobileVariantIdHolders = mobileAddToCart.querySelectorAll('[data-current-variant-id-value]');
    mobileVariantIdHolders.forEach((el) => {
      el.textContent = currentVariantId;
    });

    mobileAddToCart.dataset.currentVariantId = currentVariantId;
    mobileAddToCart.dataset.currentVariantAvailable = sourceItem.dataset.currentVariantAvailable || 'false';
  }

  static resetQuickBuyButtons(item) {
    const quickBuyWrapper = item.querySelector('.product-grid-item__quick-buy');
    const addToCartWrapper = item.querySelector('.product-grid-item__add-to-cart');
    const addToCartButton = item.querySelector('.js-add-to-cart');
    const outOfStockButton = item.querySelector('.js-out-of-stock');

    if (quickBuyWrapper) {
      quickBuyWrapper.classList.remove('d-none');
    }

    if (addToCartWrapper) {
      addToCartWrapper.classList.add('d-none');
    }

    if (addToCartButton) {
      addToCartButton.classList.remove('d-none');
    }

    if (outOfStockButton) {
      outOfStockButton.classList.add('d-none');
    }
  }

  static updateQuickBuyButtons(item) {
    if (!item) return;

    const quickBuyWrapper = item.querySelector('.product-grid-item__quick-buy');
    const addToCartWrapper = item.querySelector('.product-grid-item__add-to-cart');
    const addToCartButton = item.querySelector('.js-add-to-cart');
    const outOfStockButton = item.querySelector('.js-out-of-stock');

    const isQuickBuyOpen = item.classList.contains('product-grid-item--quick-buy');
    const isVariantAvailable = item.dataset.currentVariantAvailable === 'true';

    if (!quickBuyWrapper || !addToCartWrapper) return;

    if (!isQuickBuyOpen) {
      this.resetQuickBuyButtons(item);
    } else {
      quickBuyWrapper.classList.add('d-none');
      addToCartWrapper.classList.remove('d-none');

      if (isVariantAvailable) {
        if (addToCartButton) addToCartButton.classList.remove('d-none');
        if (outOfStockButton) outOfStockButton.classList.add('d-none');
      } else {
        if (addToCartButton) addToCartButton.classList.add('d-none');
        if (outOfStockButton) outOfStockButton.classList.remove('d-none');
      }
    }

    if (window.innerWidth < 992 && isQuickBuyOpen) {
      this.syncMobileQuickBuyState(item);
    }
  }

  static async loadProductJson(item) {
    if (!item) return null;

    const productId = item.getAttribute('data-product-id');
    const jsonUrl = item.getAttribute('data-json-url');

    if (!productId || !jsonUrl) return null;

    let holder = document.getElementById(`ProductJson${productId}`);

    if (holder?.textContent?.trim()) {
      item.dataset.jsonLoaded = '1';

      try {
        return JSON.parse(holder.textContent);
      } catch (error) {
        console.error('[product-grid] JSON parse failed:', error);
        return null;
      }
    }

    if (item.dataset.jsonLoading === '1') return null;

    item.dataset.jsonLoading = '1';

    try {
      const response = await fetch(jsonUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();

      if (!holder) {
        holder = document.createElement('script');
        holder.type = 'application/json';
        holder.id = `ProductJson${productId}`;
        item.appendChild(holder);
      }

      holder.textContent = safeStringify(data);
      item.dataset.jsonLoaded = '1';

      return data;
    } catch (error) {
      console.error('[product-grid] JSON load failed:', error);
      return null;
    } finally {
      item.dataset.jsonLoading = '0';
    }
  }

  static getProductData(item) {
    const productId = item.getAttribute('data-product-id');
    if (!productId) return null;

    const holder = document.getElementById(`ProductJson${productId}`);
    if (!holder || !holder.textContent.trim()) return null;

    try {
      return JSON.parse(holder.textContent);
    } catch (error) {
      console.error('[product-grid] JSON parse failed:', error);
      return null;
    }
  }

  static initItem(item) {
    if (!item || item.dataset.variantInit === '1') return;

    const productData = this.getProductData(item);
    if (!productData || !productData.variants || !productData.variants.length) return;

    item.dataset.variantInit = '1';

    const initialVariant =
      this.findVariant(productData, this.getCurrentSelection(item)) ||
      productData.variants.find((variant) => variant.available) ||
      productData.variants[0];

    if (!initialVariant) return;

    this.applyVariant(item, initialVariant, productData);
    this.bindColorEvents(item, productData);
    this.bindSizeEvents(item, productData);
    this.updateOptionAvailability(item, productData);
    this.updateQuickBuyButtons(item);
  }

  static bindColorEvents(item, productData) {
    item.querySelectorAll('.js-product-color').forEach((colorEl) => {
      if (colorEl.dataset.bound === '1') return;
      colorEl.dataset.bound = '1';

      colorEl.addEventListener('click', (event) => {
        event.preventDefault();

        if (colorEl.classList.contains('is-disabled')) return;

        const option = colorEl.getAttribute('data-option');
        const value = colorEl.getAttribute('data-value');

        if (!option || !value) return;

        this.setActiveOption(item, '.js-product-color', option, value);

        const selectedOptions = this.getCurrentSelection(item);
        const matchedVariant = this.findVariant(productData, selectedOptions);

        if (matchedVariant) {
          this.applyVariant(item, matchedVariant, productData);
        } else {
          this.updateOptionAvailability(item, productData);
          this.updateQuickBuyButtons(item);
          this.syncMobileQuickBuyState(item);
        }
      });
    });
  }

  static bindSizeEvents(item, productData) {
    item.querySelectorAll('.js-product-size').forEach((sizeEl) => {
      if (sizeEl.dataset.bound === '1') return;
      sizeEl.dataset.bound = '1';

      sizeEl.addEventListener('click', (event) => {
        event.preventDefault();

        if (sizeEl.classList.contains('is-disabled')) return;

        const option = sizeEl.getAttribute('data-option');
        const value = sizeEl.getAttribute('data-value');

        if (!option || !value) return;

        this.setActiveOption(item, '.js-product-size', option, value);

        const selectedOptions = this.getCurrentSelection(item);
        const matchedVariant = this.findVariant(productData, selectedOptions);

        if (matchedVariant) {
          this.applyVariant(item, matchedVariant, productData);
        } else {
          this.updateOptionAvailability(item, productData);
          this.updateQuickBuyButtons(item);
          this.syncMobileQuickBuyState(item);
        }
      });
    });
  }

  static getCurrentSelection(item) {
    const selected = {};

    item.querySelectorAll('.js-product-color.active, .js-product-size.active').forEach((el) => {
      const option = el.getAttribute('data-option');
      const value = el.getAttribute('data-value');

      if (option && value) {
        selected[option] = value;
      }
    });

    return selected;
  }

  static setActiveOption(item, selector, optionName, value) {
    item.querySelectorAll(`${selector}[data-option="${optionName}"]`).forEach((el) => {
      if (el.getAttribute('data-value') === value) {
        el.classList.add('active');
      } else {
        el.classList.remove('active');
      }
    });
  }

  static findVariant(productData, selectedOptions = {}) {
    if (!productData || !productData.variants || !productData.variants.length) return null;

    return (
      productData.variants.find((variant) => {
        return Object.entries(selectedOptions).every(([optionName, value]) => {
          return variant[optionName] === value;
        });
      }) || null
    );
  }

  static applyVariant(item, variant, passedProductData = null) {
    if (!variant) return;

    this.updatePrice(item, variant);
    this.syncActiveOptions(item, variant);
    this.updateCurrentVariantState(item, variant);
    this.updateOptionAvailability(item, passedProductData);
    this.updateQuickBuyButtons(item);
    this.syncMobileQuickBuyState(item);
  }
  
  static updatePrice(item, variant) {
    const priceOldElement = item.querySelector('.js-price-old');
    const priceNewElement = item.querySelector('.js-price-new');

    if (!priceNewElement || typeof Shopify === 'undefined' || !Shopify.formatMoney) return;

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (priceOldElement) {
        priceOldElement.classList.remove('d-none');
        priceOldElement.innerHTML = Shopify.formatMoney(variant.compare_at_price);
      }

      priceNewElement.classList.remove('d-none');
      priceNewElement.classList.add('price-new');
      priceNewElement.innerHTML = Shopify.formatMoney(variant.price);
    } else {
      if (priceOldElement) {
        priceOldElement.classList.add('d-none');
        priceOldElement.innerHTML = '';
      }

      priceNewElement.classList.remove('d-none');
      priceNewElement.classList.remove('price-new');
      priceNewElement.innerHTML = Shopify.formatMoney(variant.price);
    }
  }

  static syncActiveOptions(item, variant) {
    const optionMap = {
      option1: variant.option1,
      option2: variant.option2,
      option3: variant.option3
    };

    Object.entries(optionMap).forEach(([optionName, value]) => {
      if (!value) return;

      item
        .querySelectorAll(`.js-product-color[data-option="${optionName}"], .js-product-size[data-option="${optionName}"]`)
        .forEach((el) => {
          if (el.getAttribute('data-value') === value) {
            el.classList.add('active');
          } else {
            el.classList.remove('active');
          }
        });
    });
  }

  static updateCurrentVariantState(item, variant) {
    if (!item || !variant) return;

    const variantId = variant.id ? String(variant.id) : '';

    item.dataset.currentVariantId = variantId;
    item.dataset.currentVariantAvailable = variant.available ? 'true' : 'false';

    const hiddenVariantInput = item.querySelector('.js-current-variant-id');
    if (hiddenVariantInput) {
      hiddenVariantInput.value = variantId;
    }

    const productFormVariantInput = item.querySelector('form [name="id"]');
    if (productFormVariantInput) {
      productFormVariantInput.value = variantId;
      productFormVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const variantIdHolders = item.querySelectorAll('[data-current-variant-id-value]');
    variantIdHolders.forEach((el) => {
      el.textContent = variantId;
    });

    item.dispatchEvent(
      new CustomEvent('product:grid:variant-change', {
        bubbles: true,
        detail: {
          variant,
          variantId: variant.id,
          productId: item.getAttribute('data-product-id') || null
        }
      })
    );
  }

  static updateOptionAvailability(item, passedProductData = null) {
    const productData = passedProductData || this.getProductData(item);
    if (!productData || !productData.variants || !productData.variants.length) return;

    const currentSelection = this.getCurrentSelection(item);
    const allOptions = item.querySelectorAll('.js-product-color, .js-product-size');

    allOptions.forEach((el) => {
      const optionName = el.getAttribute('data-option');
      const value = el.getAttribute('data-value');

      if (!optionName || !value) return;

      const testSelection = { ...currentSelection, [optionName]: value };

      const hasAvailableVariant = productData.variants.some((variant) => {
        if (!variant.available) return false;

        return Object.entries(testSelection).every(([opt, val]) => {
          return variant[opt] === val;
        });
      });

      if (hasAvailableVariant) {
        el.classList.remove('is-disabled');
        el.removeAttribute('aria-disabled');
      } else {
        el.classList.add('is-disabled');
        el.setAttribute('aria-disabled', 'true');
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  ProductGridLazyVariants.init();
});

document.addEventListener('shopify:section:load', (event) => {
  const root = event?.target || document;

  root.querySelectorAll('.js-product-grid-item').forEach((item) => {
    item.removeAttribute('data-variant-init');
    item.removeAttribute('data-json-loaded');
    item.removeAttribute('data-json-loading');
    item.removeAttribute('data-current-variant-id');
    item.removeAttribute('data-current-variant-available');

    item.querySelectorAll('.js-product-color, .js-product-size').forEach((el) => {
      el.removeAttribute('data-bound');
    });
  });

  ProductGridLazyVariants.closeMobileAddToCart();

  if (typeof window.observeGridItems === 'function') {
    window.observeGridItems();
  } else {
    ProductGridLazyVariants.init();
  }
});